import { useState, useEffect, useRef, useMemo, useCallback } from "react";

import { useHistory, useLocation } from "react-router-dom"

import { 
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    Select,
    TextField,
    MenuItem,
    List,
    Typography,
    Autocomplete,
    CircularProgress,
    Alert,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import ArchiveIcon from '@mui/icons-material/Archive';
import SaveIcon from "@mui/icons-material/Save"
import PersonIcon from "@mui/icons-material/Person"
import ApartmentIcon from "@mui/icons-material/Apartment"
import SearchIcon from "@mui/icons-material/Search"
import OpenInFullIcon from "@mui/icons-material/OpenInFull"
import CloseIcon from "@mui/icons-material/Close"

import {
  saveCard,
  deleteCard,
  removeComment,
  editComment,
  archiveCard,
  unarchiveCard,
  deleteCardCallback,
  buildCustomFieldsSnapshot
} from "./functions";

import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { DateTimePicker } from "@mui/x-date-pickers";
import { number2dayweek } from "../Report/NewDetailedRegister/detailed_register_leftmenu";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import ptBR from 'dayjs/locale/pt-br'
import { ptBR as localePTBR } from '@mui/x-date-pickers/locales'

import HistoricRow from "../Crm/ContactProfile/historic_row";

import { getSession } from "../../auth"
import api from "../../api"
import Loading from '../Loading'
import ConfirmationDialogCaptcha from "../Dialogs/ConfimationDialogCaptcha"
import OpportunityAttachments from "./OpportunityAttachments"
import ClientDialog from "../Dialogs/Client"
import ContactEnterpriseDialog from "../Dialogs/ContactEnterprise"
import Event from "../Agenda/Event"

import { colorOptionsMedium } from "./index"

import dayjs from 'dayjs'
import CommentBox from "./commentbox";
import { CommentBoxTextField } from "./commentbox";
import KwikMarkdown from "../../utils/kwikmarkdown";
import ConfirmationDialog from "../Dialogs/Confirmation";
import ErrorDialog from "../Dialogs/ErrorDialog";
import UserPermissions from "../User/Permissions";
import { NumericFormatInput } from "../../utils/masks";

import Templates from "../Crm/Templates"
import SendSMS from "../Crm/TabButtons/SendMessage/SendSMS"
import SendEmail from "../Crm/TabButtons/SendMessage/SendEmail"

import formatPhone from "../../utils/format_phone"
import { generateUniqueNick  } from "../../utils/utils"
import useNotification from '../Notification'

import ChatRightSideBar from "../Chat/ChatRightSideBar"

import userPermissions from "../User/Permissions"

import sortBy from '../../utils/sortBy'
import OpportunityMarkers from "./opportunity-markers";
import OpportunityCustomFieldsSection from "./OpportunityCustomFieldsSection";
import AdvancedSearchDialog from "./advanced-search";
import { parseOpportunityHistoryRows } from "./opportunity-history";
import { handleContactCreateErrors } from "./opportunity-contact-errors";
import { getStandardFieldLabel, isStandardFieldHidden } from "./standard-fields";

dayjs.locale("pt-br")

const userGroups = userPermissions()

const OpportunityDescriptionField = ({ value, onChange, required, loading }) => {
    const [editing, setEditing] = useState(!value)
    const [expanded, setExpanded] = useState(false)
    const editedByUserRef = useRef(false)

    const hasDescription = Boolean(String(value || "").trim())

    useEffect(() => {
        if (!loading && !editedByUserRef.current) {
            setEditing(!hasDescription)
        }
    }, [hasDescription, loading])

    const descriptionPreview = (
        <Box
            className={`opportunity-description-preview ${hasDescription ? "" : "empty"}`}
            onClick={() => {
                editedByUserRef.current = true
                setEditing(true)
            }}
        >
            {hasDescription ? (
                <KwikMarkdown>{value}</KwikMarkdown>
            ) : "Clique para escrever uma descrição"}
        </Box>
    )

    return (
        <Box className="opportunity-description-field">
            <Box className="opportunity-description-field-header">
                <Typography variant="subtitle2">Descrição{required ? " *" : ""}</Typography>
                <Tooltip title="Expandir descrição">
                    <span>
                        <IconButton
                            size="small"
                            onClick={() => setExpanded(true)}
                            disabled={!hasDescription}
                        >
                            <OpenInFullIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {editing ? (
                <Box className="opportunity-description-editor">
                    <CommentBoxTextField
                        value={value}
                        onChange={({ target }) => {
                            editedByUserRef.current = true
                            onChange(target.value)
                        }}
                        id="opportunity-description"
                    />
                    <Box className="opportunity-description-editor-actions">
                        <Button size="small" variant="contained" onClick={() => setEditing(false)}>
                            Visualizar
                        </Button>
                    </Box>
                </Box>
            ) : descriptionPreview}

            <Dialog
                open={expanded}
                onClose={() => setExpanded(false)}
                fullScreen
                className="opportunity-description-modal"
            >
                <DialogTitle className="opportunity-description-modal-header">
                    <span>Descrição</span>
                    <IconButton onClick={() => setExpanded(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <KwikMarkdown>{value}</KwikMarkdown>
                </DialogContent>
            </Dialog>
        </Box>
    )
}

const Opportunity = ({ selectedJourney, allUsers, data, board, handleClose, setBoard, onDelete, onSave }) => {

    const history = useHistory()

    const myuser = getSession()
    const permissions = UserPermissions()

    const fieldLabel = (key) => getStandardFieldLabel(selectedJourney, key)
    const fieldHidden = (key) => isStandardFieldHidden(selectedJourney, key)

    const [contactOptions, setContactOptions] = useState([])
    const [loadingContacts, setLoadingContacts] = useState(false)
    const [contactInputValue, setContactInputValue] = useState("")
    const [loading, setLoading] = useState(false)
    const [enterpriseOptions, setEnterpriseOptions] = useState([])
    const [loadingEnterprises, setLoadingEnterprises] = useState(false)
    const [enterpriseInputValue, setEnterpriseInputValue] = useState("")
    const [selectedEnterprise, setSelectedEnterprise] = useState(null)
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
    const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false)
    const [showDeleteCommentConfirmation, setShowDeleteCommentConfirmation] = useState(null)
    const [showEditCommentConfirmation, setShowEditCommentConfirmation] = useState(null)
    const [editMode, setEditMode] = useState(null)
    const [attachmentBusy, setAttachmentBusy] = useState(false)
    const [showLeaveWarning, setShowLeaveWarning] = useState(false)
    const [warning, setWarning] = useState(null)
    const [error, setError] = useState(null)
    const [missingField, setMissingField] = useState(null) 

    const [tagsOpen, setTagsOpen] = useState(false)
    const [allTags, setAllTags] = useState([])
    
    const [showAdvancedContactSearch, setShowAdvancedContactSearch] = useState(false)
    const [showAdvancedEnterpriseSearch, setShowAdvancedEnterpriseSearch] = useState(false)
    
    const [customFieldsOpen, setCustomFieldsOpen] = useState(false)
    const [allCustomFields, setAllCustomFields] = useState([])
    const [showNewCustomFieldForm, setShowNewCustomFieldForm] = useState(false)
    const [selectOpen, setSelectOpen] = useState(false)
    const [showDeleteCustomFieldConfirmation, setShowDeleteCustomFieldConfirmation] = useState(null)
    const [editingCustomField, setEditingCustomField] = useState(null)

    const [pendingChanges, setPendingChanges] = useState(false)
    const [createNewContactDialog, showCreateNewContactDialog] = useState(false)

    const [ showContactProfile, setShowContactProfile ] = useState(false)
    const [ contactData, setContactData ] = useState(null)
    const [ showEnterpriseProfile, setShowEnterpriseProfile ] = useState(false)
    const [ enterpriseData, setEnterpriseData ] = useState(null)

    const [whatsAppNotifyData, setWhatsAppNotifyData] = useState({
        show          : false,
        templates     : [],
        queues        : {},
        clients       : [],
        selectionIds  : [],
        originNumbers : []
    })
    const [smsNotifyData, setSMSNotifyData] = useState({
        show      : false,
        queues    : [],
        telephone : []
    })
    const [emailNotifyData, setEmailNotifyData] = useState({
        show    : false,
        queues  : [],
        clients : []
    })
    const [eventDialogData, setEventDialogData] = useState({
        show    : false,
        contact : {}
    })


  const [dialogs, setDialogs] = useState({activeDialog: null})
  const [showContactEnterpriseDialog, setNewContactEnterprise] = useState(false)

  useEffect(() => {
    if (!attachmentBusy) return
    const handler = (e) => {
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [attachmentBusy])

  const loadDefaultContacts = async () => {
    try {
      setLoadingContacts(true);
      const enterpriseId = getSession().profile.enterprise.id;
      const contactsResponse = await api.get(
        `api/enterprises/${enterpriseId}/contacts/simplified/?limit=25&offset=0`
      );

      if (contactsResponse?.status === 200) {
        setContactOptions(
          contactsResponse.data.results || contactsResponse.data
        );
      }
    } catch (error) {
      console.error("Erro ao carregar contatos padrão:", error);
      setContactOptions([]);
    } finally {
      setLoadingContacts(false);
    }
  };

    const getColumnCardsWithoutCurrent = (columnId) => {
        const selectedColumn = board?.columns?.find((column) => Number(column.id) === Number(columnId))
        return (selectedColumn?.cards || []).filter((card) => Number(card.id) !== Number(data.card?.id))
    }

    const getColumnOpportunityCountWithoutCurrent = (columnId) => {
        const selectedColumn = board?.columns?.find((column) => Number(column.id) === Number(columnId))
        const total = Number(selectedColumn?.opportunity_count ?? selectedColumn?.cards?.length ?? 0)
        const currentColumn = data.card?.column || data.column
        const currentIsCounted = data.card?.id && Number(currentColumn) === Number(columnId)

        return Math.max(0, total - (currentIsCounted ? 1 : 0))
    }

    const initialColumn = data.card?.column || data.column || board?.columns?.[0]?.id || null
    const initialPosition = data.card?.position ?? getColumnOpportunityCountWithoutCurrent(initialColumn)

    const [state, setState] = useState({
        "column": initialColumn,
        "position": initialPosition,
        "status": "ACTIVE",
        "title": "",
        "description": "",
        "value": "",
        "currency": "BRL",
        "owner": allUsers.find(user => user.id === myuser.id)?.id || '',
        "contact": "",
        "contact_enterprise": null,
        "secondary_contacts": "",
        "history": [],
        "comments": [],
        "chats"   : [],
        "custom_fields": {},
        ...data.card
    })

    useEffect(() => {
        if (missingField) setMissingField(null)
    }, [state])

    const [newCustomField, setNewCustomField] = useState({
        "name": "",
        "field_type": "text",
        "required": false,
        "options": [],
        "placeholder": ""
    })

    const selectedColumnCards = useMemo(() => {
        return getColumnCardsWithoutCurrent(state.column)
    }, [board?.columns, state.column, state.id])

    const positionOptions = useMemo(() => {
        const positionCount = Math.max(
            selectedColumnCards.length,
            getColumnOpportunityCountWithoutCurrent(state.column)
        )
        return Array.from({ length: positionCount + 1 }, (_, index) => index)
    }, [board?.columns, selectedColumnCards.length, state.column, state.id])

    const selectedPosition = Number.isInteger(Number(state.position))
        ? Number(state.position)
        : getColumnOpportunityCountWithoutCurrent(state.column)

    useEffect(() => {
        const maxPosition = Math.max(positionOptions.length - 1, 0)
        if (selectedPosition > maxPosition) {
            setState((prevState) => ({
                ...prevState,
                position: maxPosition,
            }))
        }
    }, [positionOptions.length, selectedPosition])
    
    const newTagsRef = useRef(null)

    const newCustomFieldsRef = useRef(null)

    useEffect(() => {
      const observer = new MutationObserver(() => {
        const dropdownPaper = document.querySelector(".paper-select-dropdown")
        if (dropdownPaper) {
          const parent = dropdownPaper.closest('div[role="presentation"]')
          if (parent) {
            parent.style.zIndex = "10000"
          }
        }
      })

      observer.observe(document.body, { childList: true, subtree: true })

      return () => observer.disconnect()
    }, [])

    const mounting = useRef(true)
    const debounceTimeoutRef = useRef(null)
    const skipPendingChangesRef = useRef(false)

    // useEffect(() => {
    //     const timeout = setTimeout(() => {
    //         mounting.current = false
    //     }, 500)
    //     return () => clearTimeout(timeout)
    // }, [])

    useEffect(() => {
        if (skipPendingChangesRef.current) {
            skipPendingChangesRef.current = false
            return
        }
        if (! mounting.current)
            setPendingChanges(true)
    }, [state])

    const saveOpportunity = async () => {
        const hadId = Boolean(state.id)
        const response = await saveCard(
            selectedJourney.id,
            state,
            data.column,
            setBoard,
            allUsers,
            allTags,
            allCustomFields,
            handleClose,
            setLoading,
            selectedEnterprise
        )

        if (response.data?.id) {
            const params = new URLSearchParams(location.search)

            params.set("selectedCard", response.data.id)
            params.set("latestChanged", response.data.id)

            history.push({
                pathname: location.pathname,
                search: params.toString(),
            })
        }

        if ([200, 201].includes(response?.status)) {
            if (!hadId && response.data?.id) {
                skipPendingChangesRef.current = true
                setState(prev => ({ ...prev, id: response.data.id }))
            }
            setPendingChanges(false)
            onSave(state)
        }
    }

    useEffect(() => {
        if (state.contact && state.contact.contact_enterprise && enterpriseOptions.length > 0) {
            const enterprise = enterpriseOptions.find(ent => ent.id === state.contact.contact_enterprise)
            if (enterprise) {
                setSelectedEnterprise(enterprise)
            }
        }
    }, [state.contact, enterpriseOptions])
    
    const fetchContactsByEnterprise = useCallback(async (enterprise) => {
        setLoadingContacts(true)
        try {
            const enterpriseId = getSession().profile.enterprise.id
            let url = `api/enterprises/${enterpriseId}/contacts/?limit=25&offset=0`
            
            if (enterprise && enterprise.id) {
                url += `&contact_enterprise=${enterprise.id}`
            }
            
            const res = await api.get(url, { timeout: 15000 })
            if (res.status === 200) setContactOptions(sortBy(res.data.results || res.data, 'name'))
        } catch (error) {
            console.error('Erro ao buscar contatos:', error)
            setContactOptions([])
        } finally {
            setLoadingContacts(false)
        }
    }, [])
    
    useEffect(() => {
        if (selectedEnterprise?.id) {
            fetchContactsByEnterprise(selectedEnterprise)
        }
    }, [selectedEnterprise?.id, fetchContactsByEnterprise])
            
    const changeInputEvent = useCallback((event) => {
        if(!event) return
        if ((event.target?.value || '').length < 2) return
        
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current)
        }
        
        debounceTimeoutRef.current = setTimeout(async () => {
            setLoadingContacts(true)
            try {
                const enterpriseId = getSession().profile.enterprise.id
                let url = `api/enterprises/${enterpriseId}/contacts/?limit=25&offset=0&search=${event.target.value}`
                
                if (selectedEnterprise && selectedEnterprise.id) {
                    url += `&contact_enterprise=${selectedEnterprise.id}`
                }
                
                const res = await api.get(url, { timeout: 15000 })
                if (res.status === 200) setContactOptions(sortBy(res.data.results || res.data, 'name'))
            } catch (error) {
                setContactOptions([])
            } finally {
                setLoadingContacts(false)
            }
        }, 1000)
    }, [selectedEnterprise])

    const changeEnterpriseInputEvent = useCallback((event, value) => {
        if(!event && !value) return
        const searchValue = event?.target?.value || value || ''
        setEnterpriseInputValue(searchValue)
                    
        if (searchValue.length < 2) {
                    
            setLoadingEnterprises(true)
            try {
                const enterpriseId = getSession().profile.enterprise.id
                api.get(`api/enterprises/${enterpriseId}/contact_enterprises/simplified/?limit=25&offset=0`).then((res) => {
                    if (res.status === 200) setEnterpriseOptions(res.data.results || res.data)
                }).catch((error) => {
                    console.error('Erro ao buscar empresas:', error)
                    setEnterpriseOptions([])
                }).finally(() => {
                    setLoadingEnterprises(false)
                })
            } catch (error) {
                console.error('Erro ao buscar empresas:', error)
                setEnterpriseOptions([])
                setLoadingEnterprises(false)
            }
            return
        }

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current)
        }
        
        debounceTimeoutRef.current = setTimeout(async () => {
            setLoadingEnterprises(true)
            try {
                const enterpriseId = getSession().profile.enterprise.id
                const res = await api.get(`api/enterprises/${enterpriseId}/contact_enterprises/simplified/?limit=25&offset=0&search=${searchValue}`)
                if (res.status === 200) setEnterpriseOptions(res.data.results || res.data)
            } catch (error) {
                console.error('Erro ao buscar empresas:', error)
                setEnterpriseOptions([])
            } finally {
                setLoadingEnterprises(false)
            }
        }, 1000)
    }, [])

    const parseHistoryRows = (data) => parseOpportunityHistoryRows(data, board, state.currency, selectedJourney)

    const parseHistoryComments = (data) => {
        const return_data = []
        data.forEach(row => {

            return_data.push({
                ...row,
                id: `c-${row.id}`,
                originId: row.id,
                deletecallback: row.owner === myuser.id ? () => setShowDeleteCommentConfirmation(row) : null,
                changecallback: (value) => {row.newtext = value},
                editcallback: row.owner === myuser.id ? (command) => {
                  if (command === 'open') setEditMode(row)
                  else if (command === 'cancel') setEditMode(null)
                  else if (command === 'save') setShowEditCommentConfirmation(row)
                }
                 : null
            })
        })
        return return_data
    }

  const removeChat = useCallback(async (card_id, chat_id, onSuccess)=>{
    const enterpriseId = getSession().profile.enterprise.id
    const res = await api.post(`/api/enterprises/${enterpriseId}/journeys/${selectedJourney?.id}/opportunities/removechat/`, {
      opportunity: card_id,
      chat: chat_id
    })
    if (res.status === 200){
      useNotification("Sucesso!", "Conversa desvinculada!", "success")
      setState((prev) => ({...prev, chats: state.chats.filter(chat => chat.id !== chat_id)}))
      onSuccess()
    }
    else
      useNotification("Ops!", "Houve um erro para desvincular a conversa!", "danger")
  }, [])

  const parseChatRows = (data) => {
    const return_data= []
    data.forEach(row => return_data.push({
      id: `h-${row.id}`,
      platform_entrypoint: row.platform,
      created_at: row.time * 1000,
      messages: [],
      uniqueid: row.uniqueid,
      chatmode: true,
      deletecallback: () => setDialogs({activeDialog:
        <ConfirmationDialogCaptcha 
          open={true}
          title="Confirmação"
          description={
            <Box>
              Para desvincular a conversa, digite:{" "}
              <span style={{ fontWeight: "bold" }}>remover</span> no campo
              abaixo.
            </Box>
          }
          handleClose={() => setDialogs({})}
          handleSubmit={() => {
            removeChat(state?.id, row?.id, ()=>setDialogs({}))
            setDialogs({})
          }}
          alternateNo="Cancelar"
          alternateYes="Confirmar"
          captcha={"remover"}
        />
      })
    }))
    return return_data
  }

    useEffect(() => {
        const fetchData = async () => {
            const enterpriseId = getSession().profile.enterprise.id
            setLoading(true)
            const [tagsResponse, customFieldsResponse, opportunityResponse, enterprisesResponse, contactsResponse] = await Promise.all([
                api.get(`api/enterprises/${enterpriseId}/journeys/${selectedJourney.id}/tags/`),
                api.get(`api/enterprises/${enterpriseId}/journeys/${selectedJourney.id}/custom_fields/`),
                data?.card?.id ? api.get(`api/enterprises/${enterpriseId}/journeys/${selectedJourney.id}/opportunities/${data.card.id}/`) : null,
                api.get(`api/enterprises/${enterpriseId}/contact_enterprises/simplified/?limit=25&offset=0`),
                api.get(`api/enterprises/${enterpriseId}/contacts/simplified/?limit=25&offset=0`)
            ])
            
            if (opportunityResponse?.status === 400){
              useNotification("Ops!", "Oportunidade não encontrada!", "danger")
              deleteCardCallback(state.column, data.card.id, setBoard)
              handleClose()
            }
            
            if (tagsResponse.status === 200)
                setAllTags(tagsResponse.data)
            if (customFieldsResponse.status === 200)
                setAllCustomFields(customFieldsResponse.data)
            if (opportunityResponse?.status === 200) {

                let customFields = {}
                if (opportunityResponse.data?.custom_fields) {
                    for (const field of opportunityResponse.data.custom_fields) {
                        customFields[field.field] = field.value || ""
                    }
                }

                const formattedOpportunity = {
                    ...opportunityResponse.data,
                    value:opportunityResponse.data?.value,
                    history: parseHistoryRows(opportunityResponse.data.history),
                    comments: parseHistoryComments(opportunityResponse.data.comments),
                    chats: parseChatRows(opportunityResponse.data.chats),
                    owner: opportunityResponse.data?.owner || '',
                    custom_fields: customFields,
                    contact_enterprise: opportunityResponse.data?.contact_enterprise?.id || null,
                }

                setState(formattedOpportunity)

                if (opportunityResponse.data?.contact_enterprise) {
                    setSelectedEnterprise(opportunityResponse.data.contact_enterprise)
                }
            }
            
            if (contactsResponse?.status === 200)
                setContactOptions(sortBy(contactsResponse.data.results || contactsResponse.data, 'name'))

            if (enterprisesResponse.status === 200)
                setEnterpriseOptions(enterprisesResponse.data.results || enterprisesResponse.data)

            mounting.current = false
            setLoading(false)
        }
        fetchData()
    }, [])



    const handleSaveCustomField = async (fieldData) => {
        const enterpriseId = getSession().profile.enterprise.id
        const response = await api.post(`api/enterprises/${enterpriseId}/journeys/${selectedJourney.id}/custom_fields/`, fieldData)
        if (response.status === 201) {
            setAllCustomFields((prevState) => ([...prevState, response.data]))
            setShowNewCustomFieldForm(false)
        }
    }

    const handleEditCustomField = async (fieldData) => {
        const enterpriseId = getSession().profile.enterprise.id
        const response = await api.patch(`api/enterprises/${enterpriseId}/journeys/${selectedJourney.id}/custom_fields/${editingCustomField.id}/`, fieldData)
        if (response.status === 200) {
            setAllCustomFields((prevState) => prevState.map(field => field.id === editingCustomField.id ? response.data : field))
            setEditingCustomField(null)
            setShowNewCustomFieldForm(false)
        }
    }

    const deleteCustomField = async (fieldId) => {
        const enterpriseId = getSession().profile.enterprise.id
        const response = await api.delete(`api/enterprises/${enterpriseId}/journeys/${selectedJourney.id}/custom_fields/${fieldId}/`)

        if (response.status === 204) {
            // Remove the field from the state
            setAllCustomFields((prevState) => prevState.filter(field => field.id !== fieldId))
            
            // Remove the field value from the opportunity's custom_fields
            setState((prevState) => {
                const updatedCustomFields = { ...prevState.custom_fields }
                delete updatedCustomFields[fieldId]
                return {
                    ...prevState,
                    custom_fields: updatedCustomFields
                }
            })
        }
    }

    const updateCustomFieldValue = (fieldId, value) => {
        setState((prevState) => ({
            ...prevState,
            custom_fields: {
                ...prevState.custom_fields,
                [fieldId]: value
            }
        }))
    }

    const validateRequiredCustomFields = () => {
        const requiredFields = allCustomFields.filter(field => field.required)
        for (const field of requiredFields) {
            const value = state.custom_fields[field.id]
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                return { isValid: false, missingField: field.name }
            }
        }
        return { isValid: true }
    }

    const validateRequiredStandardFields = () => {
        const required = Array.from(new Set(['title', ...(selectedJourney?.required_opportunity_fields || [])]))
        for (const f of required) {
            switch (f) {
                case 'title':
                    if (!state.title || (typeof state.title === 'string' && state.title.trim() === '')) return { isValid: false, missingField: fieldLabel('title') }
                    break
                case 'description':
                    if (!state.description || (typeof state.description === 'string' && state.description.trim() === '')) return { isValid: false, missingField: fieldLabel('description') }
                    break
                case 'value':
                    if (state.value === null || state.value === undefined || state.value === '') return { isValid: false, missingField: fieldLabel('value') }
                    break
                case 'contact':
                    if (!state.contact) return { isValid: false, missingField: fieldLabel('contact') }
                    break
                case 'contact_enterprise':
                    if (!state.contact_enterprise) return { isValid: false, missingField: fieldLabel('contact_enterprise') }
                    break
                case 'owner':
                    if (!state.owner) return { isValid: false, missingField: fieldLabel('owner') }
                    break
                case 'estimated_at':
                    if (!state.estimated_at) return { isValid: false, missingField: fieldLabel('estimated_at') }
                    break
                case 'closed_at':
                    if (!state.closed_at) return { isValid: false, missingField: fieldLabel('closed_at') }
                    break
                case 'status':
                    if (!state.status) return { isValid: false, missingField: fieldLabel('status') }
                    break
                default:
                    break
            }
        }
        return { isValid: true }
    }

    const totalHistory = useMemo(() => [...state.history, ...state.comments, ...state.chats].toSorted((a, b) => dayjs(b.created_at) - dayjs(a.created_at)), [state.history, state.comments, state.chats])

    const getQueues = async () => {
        if (Object.keys(whatsAppNotifyData.queues).length !== 0)
            return whatsAppNotifyData.queues
        return getSession().profile.queues.reduce((acc, queue) => {
            acc[queue.description] = queue.name
            return acc
        }, {})
    }

    const getClients = async (contact) => [contact]
    const getSelectionIds = async (contact) => [contact.id]

    const getOriginNumbers = async () => {
        if (whatsAppNotifyData.originNumbers.length !== 0)
            return whatsAppNotifyData.originNumbers
        let numbers = []
        const response360Dialog = await api.get(`/api/IS/account/${getSession().profile.enterprise.accountcode}/senders/`, { timeout: 0 })
        if (response360Dialog === 200)
            numbers = response360Dialog.data
        const responseMeta = await api.get(`/api/enterprises/${getSession().profile.enterprise.id}/whatsapp/`)
        if (responseMeta.status === 200)
            responseMeta.data.forEach((each) => numbers.push(each.phone_number))
        return numbers
    }

    const setupWhatsAppNotify = async (contact, setup) => {
        setLoading(true)
        const queues = await getQueues()
        const clients = await getClients(contact)
        const selectionIds = await getSelectionIds(contact)
        const originNumbers = await getOriginNumbers()
        setLoading(false)
        setWhatsAppNotifyData((prevState) => ({
            ...prevState,
            show          : true,
            templates     : [],
            queues        : queues,
            clients       : clients,
            selectionIds  : selectionIds,
            originNumbers : originNumbers,
            setup         : setup
        }))
    }

    const setupSMSNotify = async (contact, setup) => {
        const queues = await getQueues()
        setSMSNotifyData((prevState) => ({
            ...prevState,
            show      : true,
            queues    : queues,
            telephone : [contact.telephone],
            setup     : setup
        }))
    }

    const setupEmailNotify = async (contact, setup) => {
        const queues = await getQueues()
        const clients = await getSelectionIds(contact)
        setEmailNotifyData((prevState) => ({
            ...prevState,
            show: true,
            queues: queues,
            clients: clients,
            setup: setup
        }))
    }

    const setupEventDialog = async (contact, setup) => {
        setEventDialogData((prevState) => ({
            ...prevState,
            show: true,
            contact: contact,
            setup: setup
        }))
    }

  const createEnterprise = async (data) => {
      const contactGroups = data.selectedGroups

      // Parent is sending the full state
      delete data.groupsSelectOpen
      delete data.contactGroups
      delete data.groupsLoading
      delete data.selectedGroups
      delete data.nextGroupsPointer

      data["contactGroups"] = contactGroups

      data = Object.keys(data).reduce((acc, key) => {
        const snakeKey = key.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase()
        acc[snakeKey] = data[key]
        return acc
      }, {})
      const response = await api.post(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/`, data)

      if (response.status === 201) {
        const newEnterprise = response.data;

        setEnterpriseOptions((prev) => [newEnterprise, ...prev]);

        // setEnterprises((prevEnterprises) => [
        //   newEnterprise,
        //   ...prevEnterprises
        // ])

        setSelectedEnterprise(newEnterprise);

        // 3) reflete no state do formulário de oportunidade
        setState((prevState) => ({
          ...prevState,
          contact_enterprise: newEnterprise.id,
          contact: null,
        }));

        setNewContactEnterprise(false)
        useNotification(
          "Sucesso!",
          `Empresa criada com sucesso.`,
          "success"
        )
        return
      } else if (response.status === 400) {
        const errors = response?.data?.non_field_errors ?? []
        if (errors.length) {
          if (errors.some(e => e.includes("enterprise, name"))) {
            useNotification("Atenção!", "Já existe uma empresa com esse nome.", "warning")
            return
          }
          if (errors.some(e => e.includes("enterprise, domain"))) {
            useNotification("Atenção!", "Este domínio já está em uso.", "warning")
            return
          }
        }
      }

      useNotification(
        "Ops!",
        `Algo deu errado, tente novamente.`,
        "danger"
      )
    }

    const createNewContact = async (name, email, telephone, userFields, advisor, contactEnterprise, cpf, customer_code, groups, toCreateCustomFields, notificationOptOut, owner, birthday) => {
        if (! name && ! email && ! telephone) {
            useNotification(
                "Atenção!",
                "Insira pelo menos um: nome, email, ou telefone.",
                "warning"
            )
            return
        }
        
        const formatTelephone = formatPhone(telephone)
        
        const payload = {
            nick                 : (telephone) ? formatTelephone : (email) ? email : await generateUniqueNick(),
            name                 : name,
            email                : email,
            telephone            : formatTelephone,
            user_fields          : userFields,
            advisor              : advisor,
            contact_enterprise   : contactEnterprise,
            cpf                  : cpf,
            customer_code        : customer_code,
            notification_opt_out : notificationOptOut,
            groups               : groups,
            owner                : owner === "" ? null : owner,
            birthday             : (birthday) ? dayjs(birthday, "DD/MM/YYYY").format("YYYY-MM-DDTHH:mm") : null
        }
        
        setLoading(true)
        const enterpriseId = getSession().profile.enterprise.id
        const response = await api.post(`/api/enterprises/${enterpriseId}/contacts/`, payload)
        setLoading(false)
        
        if (response.status !== 201) {
            handleContactCreateErrors(response)
            return
        }

    showCreateNewContactDialog(false)
    const createdContact = response.data

    const createdContactEnterprise = createdContact.contact_enterprise
    const contactEnterpriseId = createdContactEnterprise && (typeof createdContactEnterprise === 'object' ? createdContactEnterprise.id : createdContactEnterprise)

    setState((prevState) => ({ ...prevState, contact: createdContact, contact_enterprise: contactEnterpriseId || prevState.contact_enterprise }))

    if (contactEnterpriseId) {
      const foundEnterprise = enterpriseOptions.find(ent => ent.id === contactEnterpriseId)
      if (foundEnterprise) {
        setSelectedEnterprise(foundEnterprise)
      } else if (typeof createdContactEnterprise === 'object' && createdContactEnterprise) {
        setSelectedEnterprise(createdContactEnterprise)
      } else {
        try {
          const res = await api.get(`api/enterprises/${enterpriseId}/contact_enterprises/${contactEnterpriseId}/`)
          if (res.status === 200) {
            setSelectedEnterprise(res.data)
            setEnterpriseOptions((prev) => {
              if (prev.find && prev.find(e => e.id === res.data.id)) return prev
              return [ ...prev, res.data ]
            })
          }
        } catch (err) {
          console.error('Erro ao buscar empresa criada:', err)
        }
      }
    }
    }

    const createEvent = async (data) => {
        const response = await api.post(`api/enterprises/${getSession().profile.enterprise.id}/tasks/`, {
            ...data,
            target_date: dayjs(data.target_date).format("YYYY-MM-DDTHH:mm"),
            contact: (data.contact) ? data.contact.id : null,
            contact_enterprise: (data.contact_enterprise) ? data.contact_enterprise.id : null,
            owner: getSession().id,
            status: "SCHEDULED",
            target: (! data.target) ? getSession().id : data.target,
            is_recurrent: (data.is_recurrent === "UNIQUE") ? "" : data.is_recurrent
        })
        if (response.status === 201) {
            setEventDialogData({ show: false, contact: {} })
            await useNotification(
                "Sucesso",
                "Tarefa criada com sucesso.",
                "success"
            )
            return
        }
        await useNotification(
            "Ops",
            "Algo deu errado, tente novamente.",
            "danger"
        )
    }

    const enterprise = useMemo(() => getSession().profile.enterprise, [])

    const fetchContactData = useCallback(async () => {
      if (contactData)
        return
      setLoading(true)
      const response = await api.get(`api/enterprises/${enterprise.id}/contacts/${state.contact.id}/`)
      setLoading(false)
      if (response.status === 200)
        setContactData(response.data)
    }, [state.contact, contactData, setContactData, setLoading])

    useEffect(() => {
      setShowContactProfile(false)
      setContactData(null)
    }, [state.contact])

    const fetchEnterpriseData = useCallback(async () => {
      if (!selectedEnterprise || enterpriseData)
        return
      setLoading(true)
      const response = await api.get(`api/enterprises/${enterprise.id}/contact_enterprises/${selectedEnterprise.id}/`)
      setLoading(false)
      if (response.status === 200)
        setEnterpriseData(response.data)
    }, [selectedEnterprise, enterpriseData, setEnterpriseData, setLoading])

    useEffect(() => {
      setShowEnterpriseProfile(false)
      setEnterpriseData(null)
    }, [selectedEnterprise?.id])

    useEffect(() => {
      if (enterpriseData && selectedEnterprise) {
        setSelectedEnterprise(prev => ({
          ...prev,
          name: enterpriseData.name,
        }));
      }
    }, [enterpriseData]);

    const handleSelectedContact = (contact) => {
        setState((prevState) => ({ ...prevState, contact: contact }))
        if (contact && contact.contact_enterprise && !selectedEnterprise) {
          const contactEnterpriseId = typeof contact.contact_enterprise === 'object' 
            ? contact.contact_enterprise.id 
            : contact.contact_enterprise
          const foundEnterprise = enterpriseOptions.find(ent => ent.id === contactEnterpriseId)
          if (foundEnterprise) {
            setSelectedEnterprise(foundEnterprise)
            setState((prevState) => ({ ...prevState, contact_enterprise: foundEnterprise.id }))
          } else if (typeof contact.contact_enterprise === 'object') {
            setSelectedEnterprise(contact.contact_enterprise)
            setState((prevState) => ({ ...prevState, contact_enterprise: contact.contact_enterprise.id }))
            setEnterpriseOptions((prev) => {
              if (prev.find && prev.find(e => e.id === contact.contact_enterprise.id)) return prev
              return [...prev, contact.contact_enterprise]
            })
          } else {
            const enterpriseId = getSession().profile.enterprise.id
            api.get(`api/enterprises/${enterpriseId}/contact_enterprises/${contactEnterpriseId}/`)
              .then((res) => {
                if (res.status === 200) {
                  setSelectedEnterprise(res.data)
                  setState((prevState) => ({ ...prevState, contact_enterprise: res.data.id }))
                  setEnterpriseOptions((prev) => {
                    if (prev.find && prev.find(e => e.id === res.data.id)) return prev
                    return [...prev, res.data]
                  })
                }
              })
              .catch((err) => console.error('Erro ao buscar empresa do contato:', err))
          }
        }
    }

    const handleSelectedEnterprise = (enterprise) => {
        setSelectedEnterprise(enterprise)
        if (!enterprise) {
          loadDefaultContacts();
        } else {
          setContactOptions([]);
        }
        setState((prevState) => ({
          ...prevState,
          contact_enterprise: enterprise ? enterprise.id : null,
          contact: null,
        }))
    }

    return (
      <>
        <Loading loading={loading} />
        {state !== null && (
          <Box className="opportunity-mainframe">
            <Box className="opportunity-header">
              <Box className="opportunity-header-left">
                <Button className="opportunity-btn" onClick={() => {
                  const doLeave = () => {
                    if (state?.id) {
                      let contact_enterprise_name = null
                      if (state.contact_enterprise) {
                        if (typeof state.contact_enterprise === 'object' && state.contact_enterprise?.name) {
                          contact_enterprise_name = state.contact_enterprise.name
                        } else if (typeof state.contact_enterprise === 'number') {
                          const found = enterpriseOptions.find(e => e.id === state.contact_enterprise)
                          contact_enterprise_name = found?.name
                        }
                      }

                      setBoard(prev => {
                        const newColumns = prev.columns.map(column => ({
                          ...column,
                          cards: column.cards.map(card => {
                            if (card.id !== state.id) return card
                            const updatedCard = { ...card, title: state.title, value: state.value, description: state.description, contact: state.contact?.name || null }
                            if (contact_enterprise_name) {
                              updatedCard.contact_enterprise_name = contact_enterprise_name
                            } else {
                              delete updatedCard.contact_enterprise_name
                            }
                            const user = allUsers.find((u) => u.id === state.owner)
                            if (user) {
                              updatedCard.owner_id = state.owner
                              updatedCard.owner = `${user.first_name} ${user.last_name}`
                              updatedCard.owner_name = updatedCard.owner
                              updatedCard.contact_enterprise = state.contact_enterprise
                            }
                            updatedCard.tags = state.tags?.map((tagId) => allTags.find((tag) => tag.id === tagId)).filter((t) => t !== undefined)
                            updatedCard.custom_fields = state.custom_fields
                            updatedCard.custom_fields_snapshot = buildCustomFieldsSnapshot(state.custom_fields, allCustomFields)
                            return updatedCard
                          })
                        }))
                        return { ...prev, columns: newColumns }
                      })
                    }
                    handleClose()
                  }

                  if (attachmentBusy) {
                    setShowLeaveWarning(true)
                    return
                  }
                  doLeave()
                }}>
                  <ArrowBackIcon />
                  Voltar
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  disabled={state.id === 0}
                  className="opportunity-btn"
                  onClick={() => setShowDeleteConfirmation(true)}
                >
                  <DeleteIcon />
                  Excluir
                </Button>
                {state.archived ? (
                  <Button
                    variant="contained"
                    color="warning"
                    disabled={state.id === 0}
                    className="opportunity-btn"
                    onClick={() => {
                      unarchiveCard(
                        selectedJourney.id,
                        state,
                        state.column,
                        setBoard
                      );
                      onSave(state);
                    }}
                  >
                    <ArchiveIcon />
                    Desarquivar
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="warning"
                    disabled={state.id === 0}
                    className="opportunity-btn"
                    onClick={() => setShowArchiveConfirmation(true)}
                  >
                    <ArchiveIcon />
                    Arquivar
                  </Button>
                )}

                <Button
                  variant="contained"
                  className="opportunity-btn"
                  onClick={() => {
                    if (window.commentOpen) {
                      setWarning({
                        message:
                          "Há um comentário em edição, se desejar continuar, o comentário não será salvo.",
                        refuse: () => {
                          setWarning(null);
                        },
                        accept: () => {
                          const stdValidation = validateRequiredStandardFields();
                          if (!stdValidation.isValid) {
                            useNotification('Ops', `Campo obrigatório "${stdValidation.missingField}" deve ser preenchido.`, 'warning');
                            setError({ message: `O campo obrigatório "${stdValidation.missingField}" deve ser preenchido.` });
                            return;
                          }
                          const validation = validateRequiredCustomFields();
                          if (!validation.isValid) {
                            useNotification('Ops', `Campo obrigatório "${validation.missingField}" deve ser preenchido.`, 'warning');
                            setError({
                              message: `O campo obrigatório "${validation.missingField}" deve ser preenchido.`,
                            });
                            return;
                          }
                          saveOpportunity();
                        },
                      });
                      return;
                    }

                    const stdValidation = validateRequiredStandardFields();
                    if (!stdValidation.isValid) {
                      setMissingField(stdValidation.missingField)
                      useNotification('Ops', `Campo obrigatório "${stdValidation.missingField}" deve ser preenchido.`, 'warning');
                      return;
                    }
                    setMissingField(null)
                    const validation = validateRequiredCustomFields();
                    if (!validation.isValid) {
                      setMissingField(validation.missingField)
                      useNotification('Ops', `Campo obrigatório "${validation.missingField}" deve ser preenchido.`, 'warning');
                      return;
                    }
                    setMissingField(null)
                    saveOpportunity();
                  }}
                >
                  <SaveIcon />
                  Salvar
                </Button>
                {pendingChanges && <Alert severity="warning" sx={{ padding: "0 1rem" }}>Você possui alterações que ainda não foram salvas.</Alert>}
              </Box>
              <Box className="opportunity-header-right">
                {state.contact && (
                  <Button
                    sx={{ display: "flex", gap: .5 }}
                    onClick={async () => {
                      await fetchContactData()
                      setShowContactProfile(true)
                      setShowEnterpriseProfile(false)
                    }}
                  >
                    <PersonIcon />
                    Ver Contato
                  </Button>
                )}
                {selectedEnterprise && (
                  <Button
                    sx={{ display: "flex", gap: .5 }}
                    onClick={async () => {
                      await fetchEnterpriseData()
                      setShowContactProfile(false)
                      setShowEnterpriseProfile(true)
                    }}
                  >
                    <ApartmentIcon />
                    Ver Empresa
                  </Button>
                )}
              </Box>
            </Box>
            <Box className="opportunity-content">
              <Box className="opportunity-data">
                <LocalizationProvider
                  sx={{ justifyContent: "space-between" }}
                  dateAdapter={AdapterDayjs}
                  adapterLocale={ptBR}
                  localeText={
                    localePTBR.components.MuiLocalizationProvider.defaultProps
                      .localeText
                  }
                >
                  <Box className="selection-row">
                    <Box>Estágio: </Box>
                    <FormControl className="selection-row-select" size="small">
                      <Select
                        labelId="set-opportunity-status"
                        value={state.column}
                        onChange={(event) => {
                          const selectedColumnId = event.target.value
                          setState((prevState) => ({
                            ...prevState,
                            column: selectedColumnId,
                            position: getColumnOpportunityCountWithoutCurrent(selectedColumnId),
                          }))
                        }}
                      >
                        {board.columns.map((column) => (
                          <MenuItem key={column.id} value={column.id}>{column.title}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box className="selection-row">
                    <Box>Posição: </Box>
                    <FormControl className="selection-row-select" size="small">
                      <Select
                        labelId="set-opportunity-position"
                        value={Math.min(selectedPosition, Math.max(positionOptions.length - 1, 0))}
                        onChange={(event) =>
                          setState((prevState) => ({
                            ...prevState,
                            position: event.target.value,
                          }))
                        }
                      >
                        {positionOptions.map((position) => (
                          <MenuItem key={position} value={position}>{position + 1}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  {!fieldHidden("status") && (
                  <Box className="selection-row">
                    <Box>{fieldLabel("status")}: </Box>
                    <FormControl className="selection-row-select" size="small">
                      <Select
                        labelId="set-opportunity-status"
                        value={state.status}
                        onChange={(event) =>
                          setState((prevState) => ({
                            ...prevState,
                            status: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="ACTIVE">Ativa</MenuItem>
                        <MenuItem value="QUALIFIED">Qualificada</MenuItem>
                        <MenuItem value="CONQUERED">Conquistada</MenuItem>
                        <MenuItem value="INACTIVE">Inativa</MenuItem>
                        <MenuItem value="LOST">Perdida</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  )}
                  <Box className="date-row">
                    <Box>{fieldLabel("created_at")}</Box>
                    {dayjs
                      .utc(state.created_at)
                      .local()
                      .format("DD/MM/YYYY HH:mm")}
                  </Box>
                  {state.created_by_name && (
                    <Box className="date-row">
                      <Box>{fieldLabel("created_by")}</Box>
                      {state.created_by_name}
                    </Box>
                  )}
                  <TextField
                    size="small"
                    label={fieldLabel("title")}
                    placeholder="Nova oportunidade"
                    value={state.title}
                    onChange={(event) =>
                      setState((prevState) => ({
                        ...prevState,
                        title: event.target.value,
                      }))
                    }
                    inputProps={{ maxLength: 255 }}
                    multiline
                    minRows={1}
                    maxRows={3}
                    required
                  />
                  {!fieldHidden("description") && (
                    <OpportunityDescriptionField
                      label={fieldLabel("description")}
                      value={state.description}
                      onChange={(description) =>
                        setState((prevState) => ({
                          ...prevState,
                          description,
                        }))
                      }
                      required={selectedJourney?.required_opportunity_fields?.includes(
                        "description"
                      )}
                      loading={loading}
                    />
                  )}
                  {enterprise.opportunity_attachments_enabled && (
                    <OpportunityAttachments
                      opportunityId={state.id}
                      journeyId={selectedJourney.id}
                      disabled={!state.id || state.id === 0}
                      onBusyChange={setAttachmentBusy}
                    />
                  )}
                  {!fieldHidden("value") && (
                  <Box className="currency-row">
                    <TextField
                      size="small"
                      label={fieldLabel("value")}
                      value={state.value || ''}
                      onChange={(event) =>{
                        setState((prevState) => ({
                          ...prevState,
                          value: event.target.value,
                        }))
                     } }
                     inputProps={{max: 9999999999}}
                      InputProps={{
                        inputComponent: NumericFormatInput,
                      }}
                      required={selectedJourney?.required_opportunity_fields?.includes('value')}
                    />
                    <FormControl size="small">
                      <InputLabel id="set-opportunity-status">Moeda</InputLabel>
                      <Select
                        labelId="set-opportunity-status"
                        label="Moeda"
                        value={state.currency}
                        onChange={(event) =>
                          setState((prevState) => ({
                            ...prevState,
                            currency: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="BRL">BRL</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  )}
                  {!fieldHidden("owner") && (
                  <FormControl
                    size="small"
                    disabled={!permissions.isSupervisor}
                  >
                    <InputLabel id="set-opportunity-status">
                      {fieldLabel("owner")}
                    </InputLabel>
                    <Select
                      labelId="set-opportunity-status"
                      label={fieldLabel("owner")}
                      value={state.owner}
                      onChange={(event) =>
                        setState((prevState) => ({
                          ...prevState,
                          owner: event.target.value,
                        }))
                      }
                    >
                      {allUsers
                        .filter((user) =>
                          selectedJourney.users.includes(user.id)
                        )
                        .map((user) => (
                          <MenuItem
                            key={user.id}
                            value={user.id}
                          >{`${user.first_name} ${user.last_name}`}</MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  )}
                  {!fieldHidden("contact_enterprise") && (
                  <Box sx={{ display: "flex"}}>
                    <Autocomplete
                      sx={{ display: "flex", flex: 1 }}
                      size="small"
                      options={enterpriseOptions}
                      loading={loadingEnterprises}
                      value={selectedEnterprise}
                      inputValue={enterpriseInputValue}
                      getOptionLabel={(option) => option.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        value && option.id === value.id
                      }
                      onInputChange={(event, value) => changeEnterpriseInputEvent(event, value)}
                      onChange={(event, newValue) => handleSelectedEnterprise(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={fieldLabel("contact_enterprise")}
                        />
                      )}
                    />
                    <Tooltip title="Busca avançada">
                        <Button
                            sx={{
                                minWidth: "unset",
                                padding: .75,
                                marginLeft: 1
                            }}
                            onClick={() => setShowAdvancedEnterpriseSearch(true)}
                        >
                            <SearchIcon />
                        </Button>
                    </Tooltip>
                    <Button
                        sx={{
                            minWidth: "unset",
                            padding: .75
                        }}
                        onClick={() => setNewContactEnterprise(true)}
                    >
                        <AddIcon />
                    </Button>
                  </Box>
                  )}

                  {!fieldHidden("contact") && (
                  <Box sx={{ display: "flex" }}>
                    <Autocomplete
                      sx={{ display: "flex", flex: 1 }}
                      size="small"
                      options={sortBy(contactOptions, 'name')}
                      loading={loadingContacts}
                      value={state.contact || null}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      //filterOptions={(options) => options}
                      onInputChange={(event, value) => changeInputEvent(event)}
                      onChange={(event, newValue) => handleSelectedContact(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={fieldLabel("contact")}
                          error={missingField === fieldLabel('contact')}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingContacts ? <CircularProgress size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    <Tooltip title="Busca avançada">
                        <Button
                            sx={{
                                minWidth: "unset",
                                padding: .75,
                                marginLeft: 1
                            }}
                            onClick={() => setShowAdvancedContactSearch(true)}
                        >
                            <SearchIcon />
                        </Button>
                    </Tooltip>
                    <Button
                        sx={{
                            minWidth: "unset",
                            padding: .75
                        }}
                        onClick={() => showCreateNewContactDialog(true)}
                    >
                        <AddIcon />
                    </Button>
                  </Box>
                  )}

                  {!fieldHidden("secondary_contacts") && (
                  <TextField
                    size="small"
                    label={fieldLabel("secondary_contacts")}
                    value={state.secondary_contacts}
                    error={missingField === fieldLabel('secondary_contacts')}
                    onChange={(event) =>{
                      setMissingField(null)
                      setState((prevState) => ({
                        ...prevState,
                        secondary_contacts: event.target.value,
                      }))
                    }}
                  />
                  )}
                  {(!fieldHidden("estimated_at") || !fieldHidden("closed_at")) && (
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                    {!fieldHidden("estimated_at") && (
                    <DemoContainer
                      components={["DatePicker"]}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingTop: 0,
                        overflow: "unset",
                      }}
                    >
                      <Box>{fieldLabel("estimated_at")}</Box>
                      <DateTimePicker
                        sx={{ minWidth: "8rem" }}
                        views={["day", "month", "year", "hours", "minutes"]}
                        format={"DD/MM/YYYY HH:mm"}
                        timeSteps={1}
                        slotProps={{
                          textField: { size: "small", sx: { width: "60%" } },
                        }}
                        value={
                          state.estimated_at
                            ? dayjs.utc(state.estimated_at).local()
                            : null
                        }
                        onChange={(event) => {
                          if (event)
                            setState((prevState) => ({
                              ...prevState,
                              estimated_at: event,
                            }));
                        }}
                        dayOfWeekFormatter={(n, date) => number2dayweek[date.$W]}
                      />
                    </DemoContainer>
                    )}
                    {!fieldHidden("closed_at") && (
                    <DemoContainer
                      components={["DatePicker"]}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingTop: 0,
                        overflow: "unset",
                      }}
                    >
                      <Box>{fieldLabel("closed_at")}</Box>
                      <DateTimePicker
                        sx={{ minWidth: "8rem" }}
                        views={["day", "month", "year", "hours", "minutes"]}
                        format={"DD/MM/YYYY HH:mm"}
                        timeSteps={1}
                        slotProps={{
                          textField: { size: "small", sx: { width: "60%" } },
                        }}
                        value={
                          state.closed_at
                            ? dayjs.utc(state.closed_at).local()
                            : null
                        }
                        onChange={(event) => {
                          if (event)
                            setState((prevState) => ({
                              ...prevState,
                              closed_at: event,
                            }));
                        }}
                        dayOfWeekFormatter={(n, date) => number2dayweek[date.$W]}
                      />
                    </DemoContainer>
                    )}
                  </LocalizationProvider>
                  )}
                  <Box className="tags-container">
                    <Box>
                      Marcadores
                      <IconButton
                        ref={newTagsRef}
                        onClick={() => setTagsOpen((prev) => !prev)}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                    <Box className="journey-card-labellist">
                      {state.tags?.map((tagId) => {
                        const tag = allTags.find((t) => t.id === tagId);
                        if (!tag) return null;
                        return (
                          <Box
                            key={tag.id}
                            sx={{
                              padding: ".15rem .25rem",
                              borderRadius: ".25rem",
                              fontSize: ".8em",
                              backgroundColor: tag.color,
                            }}
                          >
                            {tag.name}
                          </Box>
                        );
                      })}
                    </Box>
                      <OpportunityMarkers 
                        tagsOpen={tagsOpen} 
                        setTagsOpen={setTagsOpen} 
                        allTags={allTags} 
                        newTagsRef={newTagsRef}
                        setAllTags={setAllTags} 
                        setParent={setState} 
                        parent={state} 
                        selectedJourney={selectedJourney}
                      />
                  </Box>

                  {/* Custom Fields Section */}
                  <OpportunityCustomFieldsSection
                    allCustomFields={allCustomFields}
                    customFieldsOpen={customFieldsOpen}
                    deleteCustomField={deleteCustomField}
                    editingCustomField={editingCustomField}
                    handleEditCustomField={handleEditCustomField}
                    handleSaveCustomField={handleSaveCustomField}
                    newCustomFieldsRef={newCustomFieldsRef}
                    selectOpen={selectOpen}
                    setCustomFieldsOpen={setCustomFieldsOpen}
                    setEditingCustomField={setEditingCustomField}
                    setSelectOpen={setSelectOpen}
                    setShowDeleteCustomFieldConfirmation={setShowDeleteCustomFieldConfirmation}
                    setShowNewCustomFieldForm={setShowNewCustomFieldForm}
                    showDeleteCustomFieldConfirmation={showDeleteCustomFieldConfirmation}
                    showNewCustomFieldForm={showNewCustomFieldForm}
                    state={state}
                    updateCustomFieldValue={updateCustomFieldValue}
                  />
                </LocalizationProvider>
              </Box>
              <Box className="opportunity-historic">
                <CommentBox
                  cardId={state.id}
                  disabled={state.id === 0}
                  JourneyId={selectedJourney.id}
                  onSubmit={(data) => {
                    setState((prev) => ({
                      ...prev,
                      comments: [
                        ...state.comments,
                        ...parseHistoryComments([data]),
                      ],
                    }));
                  }}
                />
                {dialogs.activeDialog}
                <List className="historic-list-gap">
                  {totalHistory.map((row) => (
                    <HistoricRow
                      key={`historic-unique-row-${row.id}`}
                      data={row}
                      contact={state}
                      setParent={(newstate)=>setDialogs(prev => ({...prev, ...newstate}))}
                      initialCollapse={false}
                      editMode={editMode}
                    />
                  ))}
                </List>
              </Box>
              {showContactProfile &&
                <Box className="opportunity-contact">
                  <ChatRightSideBar
                    closeChatRightSideBar={() => setShowContactProfile(false)}
                    agentCanUpdateContact={enterprise.settings.agent_can_update_contact}
                    enterprise={enterprise.id}
                    contactData={contactData}
                    isManageCalls={false}
                    fetchHistoryMessages={(row) => console.log("[fetchHistoryMessages]", row)}
                    allLabels={[]}
                    chatLabels={[]}
                    setChatLabels={[]}
                    chat={null}
                    reloadContact={() => console.log("[reloadContact]")}
                    chatCursor={null}
                    allGroups={[]}
                    setChatGroups={() => console.log("[setChatGroups")}
                    parent={{
                      templates : [],
                      queues    : []
                    }}
                    fetchScoutMessages={(row) => console.log("[fetchScoutMessages]")}
                    profileContent="contact_data"
                    navigation={() => {
                        history.push({
                            pathname: "/chat",
                            search: `selectedTab=contacts&selectedContact=${contactData?.id}`,
                        });
                    }}
                  />
                </Box>
              }
              {showEnterpriseProfile &&
                <Box className="opportunity-contact">
                  <ChatRightSideBar
                    closeChatRightSideBar={() => setShowEnterpriseProfile(false)}
                    enterprise={enterprise.id}
                    enterpriseData={enterpriseData}
                    setEnterpriseData={setEnterpriseData}
                    isManageCalls={false}
                    profileContent="enterprise_data"
                    navigation={() => {
                        history.push({
                            pathname: "/chat",
                            search: `selectedTab=enterprises&selectedEnterprise=${enterpriseData?.id}`,
                        });
                    }}
                  />
                </Box>
              }
            </Box>
          </Box>
        )}
        {showDeleteConfirmation && (
          <ConfirmationDialogCaptcha
            open={showDeleteConfirmation}
            title="Confirmação"
            description={
              <Box>
                Para remover a oportunidade {state.name}, digite:{" "}
                <span style={{ fontWeight: "bold" }}>remover</span> no campo
                abaixo.
              </Box>
            }
            handleClose={() => setShowDeleteConfirmation(false)}
            handleSubmit={() => {
              deleteCard(selectedJourney.id, state, state.column, setBoard);
              onDelete(state);
            }}
            captcha={"remover"}
          />
        )}
        {showLeaveWarning && (
          <ConfirmationDialog
            open={showLeaveWarning}
            title="Envio em andamento"
            description="Há um arquivo sendo enviado. Se você sair agora, perderá o anexo. Deseja sair mesmo assim?"
            handleClose={() => setShowLeaveWarning(false)}
            handleSubmit={() => {
              setShowLeaveWarning(false)
              handleClose()
            }}
          />
        )}
        {showArchiveConfirmation && (
          <ConfirmationDialogCaptcha
            open={showArchiveConfirmation}
            title="Confirmação"
            description={
              <Box>
                Para arquivar a oportunidade {state.name}, digite:{" "}
                <span style={{ fontWeight: "bold" }}>arquivar</span> no campo
                abaixo.
              </Box>
            }
            handleClose={() => setShowArchiveConfirmation(false)}
            handleSubmit={() => {
              archiveCard(selectedJourney.id, state, state.column, setBoard);
              onDelete(state);
            }}
            captcha={"arquivar"}
          />
        )}
        {showDeleteCommentConfirmation && (
          <ConfirmationDialogCaptcha
            open={showDeleteCommentConfirmation}
            title="Confirmação"
            description={
              <Box>
                Para remover o comentário digite:{" "}
                <span style={{ fontWeight: "bold" }}>remover</span> no campo
                abaixo.
              </Box>
            }
            handleClose={() => setShowDeleteCommentConfirmation(null)}
            handleSubmit={() => {
              removeComment(
                selectedJourney,
                showDeleteCommentConfirmation.id,
                () => {
                  setState((prev) => ({
                    ...prev,
                    comments: state.comments.filter(
                      (comment) =>
                        comment.originId !== showDeleteCommentConfirmation.id
                    ),
                  }));
                  setShowDeleteCommentConfirmation(null);
                }
              );
            }}
            captcha={"remover"}
          />
        )}
        {showEditCommentConfirmation && (
          <ConfirmationDialog
            open={true}
            title="Confirmação"
            description={
              <Box>
                Confirmar alteração do comentário?
              </Box>
            }
            handleClose={() => setShowEditCommentConfirmation(null)}
            handleSubmit={() => {
              editComment(
                selectedJourney,
                showEditCommentConfirmation.id,
                showEditCommentConfirmation.newtext,
                () => {
                  setState((prev) => ({
                    ...prev,
                    comments: state.comments.map(
                      (comment) => {
                        if (comment.originId !== showEditCommentConfirmation.id) return comment
                        comment.text = showEditCommentConfirmation.newtext
                        return comment
                      } 
                    ),
                  }));
                  setShowEditCommentConfirmation(null);
                  setEditMode(null)
                }
              );
            }}
          />
        )}
        {warning && (
          <ConfirmationDialog
            open={true}
            title={"Atenção!"}
            description={warning.message}
            handleClose={warning.refuse}
            handleSubmit={warning.accept}
          />
        )}
        {error && (
          <ErrorDialog
            open={true}
            message={error.message}
            handleClose={() => setError(null)}
          />
        )}
        {whatsAppNotifyData.show && ! whatsAppNotifyData.setup && (
          <Templates
            title="Enviar whatsapp ao contato"
            open={whatsAppNotifyData.show}
            templates={whatsAppNotifyData.templates}
            queues={whatsAppNotifyData.queues}
            clients={whatsAppNotifyData.clients}
            selectionIds={whatsAppNotifyData.selectionIds}
            originNumbers={whatsAppNotifyData.originNumbers}
            handleClose={() =>
              setWhatsAppNotifyData((prevState) => ({
                ...prevState,
                show: false,
              }))
            }
            handlePostSuccess={() => {}}
            positionFixed
            setup={whatsAppNotifyData.setup}
          />
        )}
        {smsNotifyData.show && ! smsNotifyData.setup && (
          <SendSMS
            queues={smsNotifyData.queues}
            selectedNumberModel={smsNotifyData.telephone}
            handleClose={() =>
              setSMSNotifyData((prevState) => ({ ...prevState, show: false }))
            }
            positionFixed
            title={
              smsNotifyData.setup
                ? "Configurar notificação de sms"
                : "Enviar SMS ao contato"
            }
            setup={smsNotifyData.setup}
          />
        )}
        {emailNotifyData.show && ! emailNotifyData.setup && (
          <SendEmail
            title={
              emailNotifyData.setup
                ? "Configurar notificação de e-mail"
                : "Enviar e-mail ao contato"
            }
            queues={emailNotifyData.queues}
            clients={emailNotifyData.clients}
            handleClose={() =>
              setEmailNotifyData((prevState) => ({ ...prevState, show: false }))
            }
            positionFixed
            setup={emailNotifyData.setup}
            canCopy={true}
          />
        )}
        {createNewContactDialog &&
            <ClientDialog 
                open={true}
                title="Novo cliente"
                type="create"
                model={[]}
                key={'ClientCreate'}
                buttonText="Adicionar"
                handleClose={() => showCreateNewContactDialog(false)}
                submit={createNewContact}
                is_financial_market_company={false}
                isAdmin={false}
                listOfUsers={[getSession()]}
                groups={[]}
                customFields={[]}
            />
        }
        {eventDialogData.show && (
          <Event
            open={eventDialogData.show}
            title="Criar Evento"
            handleClose={() => setEventDialogData({ show: false, contact: {} })}
            handleSubmit={createEvent}
            isSupervisor={userGroups.isSupervisor}
            myTeam={[]}
            selectedEvent={{ contact_data: eventDialogData.contact }}
            setProfile={() => {}}
            setupWhatsAppNotify={(_, setup) => setupWhatsAppNotify(eventDialogData.contact, setup)}
            setupSMSNotify={(_, setup) => setupSMSNotify(eventDialogData.contact, setup)}
            setupEmailNotify={(_, setup) => setupEmailNotify(eventDialogData.contact, setup)}
            hideNotifyRow={true}
            lock
          />
        )}
        {whatsAppNotifyData.show && whatsAppNotifyData.setup &&
            <Templates
                title={whatsAppNotifyData.setup ? "Configurar notificação de whatsapp" : "Enviar whatsapp ao contato"}
                open={whatsAppNotifyData.show}
                templates={whatsAppNotifyData.templates}
                queues={whatsAppNotifyData.queues}
                clients={whatsAppNotifyData.clients}
                selectionIds={whatsAppNotifyData.selectionIds}
                originNumbers={whatsAppNotifyData.originNumbers}
                handleClose={() => setWhatsAppNotifyData((prevState) => ({
                    ...prevState, 
                    show: false,
                    setup: null
                }))}
                handlePostSuccess={() => { }}
                positionFixed
                setup={whatsAppNotifyData.setup}
            />
        }
        {smsNotifyData.show && smsNotifyData.setup &&
            <SendSMS
                queues={smsNotifyData.queues}
                selectedNumberModel={smsNotifyData.telephone}
                handleClose={() => setSMSNotifyData((prevState) => ({
                    ...prevState, 
                    show: false,
                    setup: null
                }))}
                positionFixed
                title={smsNotifyData.setup ? "Configurar notificação de sms" : "Enviar SMS ao contato"}
                setup={smsNotifyData.setup}
            />
        }
        {emailNotifyData.show && emailNotifyData.setup &&
            <SendEmail
                title={emailNotifyData.setup ? "Configurar notificação de e-mail" : "Enviar e-mail ao contato"}
                queues={emailNotifyData.queues}
                clients={emailNotifyData.clients}
                handleClose={() => setEmailNotifyData((prevState) => ({
                    ...prevState, 
                    show: false,
                    setup: null
                }))}
                positionFixed
                setup={emailNotifyData.setup}
                canCopy={true}
            />
        }
        {showContactEnterpriseDialog &&
          <ContactEnterpriseDialog
            open={showContactEnterpriseDialog}
            handleClose={() => setNewContactEnterprise(false)}
            createEnterprise={createEnterprise} 
            updateEnterprise={() => {}}
            selectedEnterprise={null}
          />
        }
        {(showAdvancedContactSearch || showAdvancedEnterpriseSearch) &&
            <AdvancedSearchDialog
                open={true}
                type={showAdvancedContactSearch ? "contact" : "enterprise"}
                enterpriseId={getSession().profile.enterprise.id}
                onClose={() => {
                    setShowAdvancedContactSearch(false);
                    setShowAdvancedEnterpriseSearch(false);
                }}
                onSelect={(item) => showAdvancedContactSearch
                    ? handleSelectedContact(item)
                    : handleSelectedEnterprise(item)
                }
            />
        }
      </>
    );
}

export default Opportunity
